import { authFetch } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Target, ArrowLeft, ChevronRight, Flag, MapPin,
  Check, Clock, X, Trophy
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = window.location.origin;

// Dynamic hole segmentation based on total holes
function buildHoleGroups(totalHoles) {
  if (totalHoles <= 3) return [{ start: 1, end: totalHoles, label: `Holes 1-${totalHoles}` }];
  if (totalHoles <= 6) return [
    { start: 1, end: 3, label: "Holes 1-3" },
    { start: 4, end: totalHoles, label: `Holes 4-${totalHoles}` }
  ];
  if (totalHoles <= 9) return [
    { start: 1, end: 3, label: "Holes 1-3" },
    { start: 4, end: 6, label: "Holes 4-6" },
    { start: 7, end: totalHoles, label: `Holes 7-${totalHoles}` }
  ];
  return [
    { start: 1, end: 6, label: "Holes 1-6" },
    { start: 7, end: 12, label: "Holes 7-12" },
    { start: 13, end: totalHoles, label: `Holes 13-${totalHoles}` }
  ];
}

export default function OnCourseSession() {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("pre-round");
  const [holeGroup, setHoleGroup] = useState(0);
  const [notes, setNotes] = useState({ preRound: {}, holes: [], postRound: {} });
  const [complete, setComplete] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-fetch on missionId change
  useEffect(() => { fetchMission(); }, [missionId]);

  const fetchMission = async () => {
    try {
      const res = await authFetch(`${API}/api/missions/${missionId}`);
      if (res.ok) {
        const data = await res.json();
        setMission(data);
        const totalHoles = getCourseHoles(data);
        const groups = buildHoleGroups(totalHoles);
        setNotes(prev => ({ ...prev, holes: groups.map(() => ({})) }));
      } else { toast.error("Mission not found"); navigate("/dashboard"); }
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  function getCourseHoles(m) {
    if (!m) return 18;
    const courseBlock = (m.session_blocks || []).find(b => b.location === "course");
    if (courseBlock?.holes) return courseBlock.holes;
    return 18;
  }

  function getCourseMode(m) {
    if (!m) return "practice";
    return m.course_mode || (m.session_blocks || []).find(b => b.location === "course")?.course_mode || "practice";
  }

  const totalHoles = getCourseHoles(mission);
  const holeGroups = buildHoleGroups(totalHoles);
  const courseBlock = (mission?.session_blocks || []).find(b => b.location === "course");
  const formatName = courseBlock?.format_name || "";
  const courseMode = getCourseMode(mission);
  const courseName = mission?.course_name || courseBlock?.course_name || "";
  const focusArea = courseBlock?.focus_area || "";

  // ─── PRACTICE ROUND CONSTANTS ───
  const PRE_ROUND_FOCUS = [
    { id: "driving", label: "Driving Accuracy" },
    { id: "approach", label: "Approach Shots" },
    { id: "short_game", label: "Short Game" },
    { id: "putting", label: "Putting" },
    { id: "course_mgmt", label: "Course Management" }
  ];

  const HOLE_RATINGS = [
    { value: "great", label: "Great", color: "bg-emerald-500/20 border-emerald-500/30 text-emerald-700" },
    { value: "solid", label: "Solid", color: "bg-blue-500/20 border-blue-500/30 text-blue-700" },
    { value: "mixed", label: "Mixed", color: "bg-amber-500/20 border-amber-500/30 text-[#111]" },
    { value: "rough", label: "Rough", color: "bg-red-500/20 border-red-500/30 text-red-500" }
  ];

  const MISS_TYPES = ["Thin", "Fat", "Push", "Pull", "Slice", "Hook", "Short", "Long"];

  // ─── NOTES HELPERS ───
  const togglePreFocus = (id) => {
    setNotes(prev => {
      const current = prev.preRound.focus || [];
      const updated = current.includes(id) ? current.filter(f => f !== id) : [...current, id];
      return { ...prev, preRound: { ...prev.preRound, focus: updated } };
    });
  };

  const updateHoleNotes = (field, value) => {
    setNotes(prev => {
      const updated = [...prev.holes];
      if (!updated[holeGroup]) updated[holeGroup] = {};
      updated[holeGroup] = { ...updated[holeGroup], [field]: value };
      return { ...prev, holes: updated };
    });
  };

  const toggleHoleMiss = (miss) => {
    const current = notes.holes[holeGroup]?.misses || [];
    const updated = current.includes(miss) ? current.filter(m => m !== miss) : [...current, miss];
    updateHoleNotes("misses", updated);
  };

  const completeRound = async () => {
    setComplete(true);
    try {
      await authFetch(`${API}/api/sessions/${missionId}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          feedback: notes,
          session_type: "on_course",
          course_mode: courseMode,
          total_holes: totalHoles,
          course_name: courseName,
          hole_segments: holeGroups.map((g, i) => ({
            ...g,
            feedback: notes.holes[i] || {}
          })),
          phase_data: { preRound: notes.preRound, holes: notes.holes, postRound: notes.postRound }
        })
      });
      toast.success("Round logged!");
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ─── COMPLETION SCREEN ───
  if (complete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full text-center">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${courseMode === "competitive" ? "bg-emerald-500/20" : "bg-purple-500/20"}`}>
            {courseMode === "competitive" ? <Trophy className="w-12 h-12 text-emerald-700" /> : <Flag className="w-12 h-12 text-purple-700" />}
          </div>
          <h1 className="text-3xl font-bold text-[#111] mb-2" data-testid="round-complete-title">
            {courseMode === "competitive" ? "Round Complete!" : "Practice Round Complete!"}
          </h1>
          <p className="text-[#555] mb-1">{totalHoles} holes{courseName ? ` at ${courseName}` : ""}{formatName && courseMode === "practice" ? ` · ${formatName}` : ""}</p>
          <p className="text-[#555] text-sm mb-8">
            {courseMode === "competitive" ? "Great work out there. Your round has been logged." : "Great practice session. Your data has been logged."}
          </p>

          {/* Summary */}
          <div className="space-y-3 mb-8 text-left">
            {/* Competitive: show score if entered */}
            {courseMode === "competitive" && notes.postRound.score && (
              <div className="bg-gray-100 rounded-xl p-4 text-center">
                <p className="text-[#555] text-xs mb-1">Score</p>
                <p className="text-3xl font-bold text-[#111]">{notes.postRound.score}</p>
              </div>
            )}
            {/* Practice: show focus areas */}
            {notes.preRound.focus?.length > 0 && (
              <div className="bg-gray-100 rounded-xl p-4">
                <p className="text-[#555] text-xs mb-2">Focus Areas</p>
                <div className="flex flex-wrap gap-2">
                  {notes.preRound.focus.map(f => (
                    <Badge key={f} className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 capitalize">{f.replace("_", " ")}</Badge>
                  ))}
                </div>
              </div>
            )}
            {/* Hole group ratings (practice mode) */}
            {courseMode === "practice" && holeGroups.map((g, i) => notes.holes[i]?.rating && (
              <div key={i} className="bg-gray-100 rounded-xl p-4 flex items-center justify-between">
                <span className="text-[#555] text-sm">{g.label}</span>
                <Badge className={HOLE_RATINGS.find(r => r.value === notes.holes[i].rating)?.color || ""}>{notes.holes[i].rating}</Badge>
              </div>
            ))}
          </div>

          <Button onClick={() => navigate("/dashboard")} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-lg" data-testid="oncourse-done-btn">
            Back to Dashboard
          </Button>
        </motion.div>
      </div>
    );
  }

  // ─── COMPETITIVE ROUND UI ───
  if (courseMode === "competitive") {
    return <CompetitiveRound
      missionId={missionId}
      totalHoles={totalHoles}
      courseName={courseName}
      focusArea={focusArea}
      notes={notes}
      setNotes={setNotes}
      completeRound={completeRound}
      navigate={navigate}
    />;
  }

  // ─── PRACTICE ROUND UI (existing flow) ───
  const progressPct = phase === "pre-round" ? 10 : phase === "on-course" ? 20 + ((holeGroup + 1) / holeGroups.length) * 60 : 90;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="px-4 py-3 border-b border-gray-200 safe-area-top">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <button onClick={() => { if (window.confirm("Exit round?")) navigate("/dashboard"); }} className="text-[#555] hover:text-[#111] p-1" data-testid="practice-exit-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center flex-1">
            <p className="text-xs text-purple-700 uppercase tracking-wider font-medium">Practice Round</p>
            <Progress value={progressPct} className="w-28 h-1 mt-1 bg-gray-200 mx-auto" />
          </div>
          <button onClick={() => { if (window.confirm("Exit round?")) navigate("/dashboard"); }} className="text-[#555] hover:text-[#111] p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="max-w-lg mx-auto">
          <AnimatePresence mode="wait">
            {/* PRE-ROUND */}
            {phase === "pre-round" && (
              <motion.div key="pre" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6">
                <div>
                  <h1 className="text-2xl font-bold text-[#111] mb-1">Pre-Round Setup</h1>
                  <p className="text-[#555] text-sm">{totalHoles} holes · {formatName || "Practice Round"}</p>
                </div>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">What do you want to focus on today?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {PRE_ROUND_FOCUS.map(f => (
                        <button key={f.id} onClick={() => togglePreFocus(f.id)}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            (notes.preRound.focus || []).includes(f.id)
                              ? "bg-purple-500/15 border-purple-500/40"
                              : "bg-gray-100 border-gray-200 hover:bg-gray-200"
                          }`} data-testid={`focus-${f.id}`}
                        >
                          <span className="text-sm text-[#111]">{f.label}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">Quick strategy note (optional)</p>
                    <textarea
                      value={notes.preRound.strategy || ""}
                      onChange={e => setNotes(prev => ({ ...prev, preRound: { ...prev.preRound, strategy: e.target.value } }))}
                      placeholder="e.g., Stay patient on the front 9, commit to every shot..."
                      className="w-full bg-gray-100 border border-gray-200 rounded-xl p-3 text-[#111] text-sm placeholder:text-[#555] resize-none h-20 focus:outline-none focus:border-purple-500/50"
                      data-testid="strategy-note"
                    />
                  </CardContent>
                </Card>

                <Button onClick={() => setPhase("on-course")} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5 text-base" data-testid="start-round-btn">
                  Start Your Round <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            )}

            {/* ON-COURSE — Dynamic hole segments */}
            {phase === "on-course" && (
              <motion.div key={`course-${holeGroup}`} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-[#111] mb-1" data-testid="hole-group-label">{holeGroups[holeGroup]?.label}</h1>
                    <p className="text-[#555] text-sm">Check-in: How are you playing?</p>
                  </div>
                  <Badge className="bg-purple-500/20 text-purple-700 border-purple-500/30" data-testid="hole-group-counter">
                    {holeGroup + 1}/{holeGroups.length}
                  </Badge>
                </div>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">Overall feel for these holes?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {HOLE_RATINGS.map(r => (
                        <button key={r.value} onClick={() => updateHoleNotes("rating", r.value)}
                          className={`p-3 rounded-xl border text-center transition-all ${
                            notes.holes[holeGroup]?.rating === r.value
                              ? r.color + " ring-1 ring-white/10"
                              : "bg-gray-100 border-gray-200 hover:bg-gray-200"
                          }`} data-testid={`hole-rating-${r.value}`}
                        >
                          <span className={`text-sm font-medium ${notes.holes[holeGroup]?.rating === r.value ? "" : "text-[#111]"}`}>{r.label}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">Common misses? (tap all that apply)</p>
                    <div className="flex flex-wrap gap-2">
                      {MISS_TYPES.map(m => (
                        <button key={m} onClick={() => toggleHoleMiss(m)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                            (notes.holes[holeGroup]?.misses || []).includes(m)
                              ? "bg-amber-500/20 border-amber-500/30 text-[#111]"
                              : "bg-gray-100 border-gray-200 text-[#555] hover:bg-gray-200"
                          }`} data-testid={`miss-${m.toLowerCase()}`}
                        >{m}</button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">Quick notes (optional)</p>
                    <textarea
                      value={notes.holes[holeGroup]?.notes || ""}
                      onChange={e => updateHoleNotes("notes", e.target.value)}
                      placeholder="Anything to remember..."
                      className="w-full bg-gray-100 border border-gray-200 rounded-xl p-3 text-[#111] text-sm placeholder:text-[#555] resize-none h-16 focus:outline-none focus:border-purple-500/50"
                    />
                  </CardContent>
                </Card>

                <div className="flex gap-3">
                  <Button variant="ghost" onClick={() => holeGroup > 0 ? setHoleGroup(holeGroup - 1) : setPhase("pre-round")} className="text-[#555] px-6">
                    Back
                  </Button>
                  <Button
                    onClick={() => holeGroup < holeGroups.length - 1 ? setHoleGroup(holeGroup + 1) : setPhase("post-round")}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-5"
                    data-testid="next-holes-btn"
                  >
                    {holeGroup < holeGroups.length - 1 ? `Next: ${holeGroups[holeGroup + 1].label}` : "Post-Round Review"}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* POST-ROUND */}
            {phase === "post-round" && (
              <motion.div key="post" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6">
                <div>
                  <h1 className="text-2xl font-bold text-[#111] mb-1">Post-Round Review</h1>
                  <p className="text-[#555] text-sm">Reflect on your round while it's fresh</p>
                </div>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">What went well today?</p>
                    <textarea
                      value={notes.postRound.positives || ""}
                      onChange={e => setNotes(prev => ({ ...prev, postRound: { ...prev.postRound, positives: e.target.value } }))}
                      placeholder="e.g., Drove well, chipping was solid..."
                      className="w-full bg-gray-100 border border-gray-200 rounded-xl p-3 text-[#111] text-sm placeholder:text-[#555] resize-none h-20 focus:outline-none focus:border-purple-500/50"
                      data-testid="post-positives"
                    />
                  </CardContent>
                </Card>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">What needs work?</p>
                    <textarea
                      value={notes.postRound.improvements || ""}
                      onChange={e => setNotes(prev => ({ ...prev, postRound: { ...prev.postRound, improvements: e.target.value } }))}
                      placeholder="e.g., 3-putted twice, distance control from 150m..."
                      className="w-full bg-gray-100 border border-gray-200 rounded-xl p-3 text-[#111] text-sm placeholder:text-[#555] resize-none h-20 focus:outline-none focus:border-purple-500/50"
                      data-testid="post-improvements"
                    />
                  </CardContent>
                </Card>

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">One thing to focus on next session?</p>
                    <textarea
                      value={notes.postRound.next_focus || ""}
                      onChange={e => setNotes(prev => ({ ...prev, postRound: { ...prev.postRound, next_focus: e.target.value } }))}
                      placeholder="e.g., Practice lag putting, work on 60-yard pitch..."
                      className="w-full bg-gray-100 border border-gray-200 rounded-xl p-3 text-[#111] text-sm placeholder:text-[#555] resize-none h-20 focus:outline-none focus:border-purple-500/50"
                      data-testid="post-next-focus"
                    />
                  </CardContent>
                </Card>

                <div className="flex gap-3">
                  <Button variant="ghost" onClick={() => { setPhase("on-course"); setHoleGroup(holeGroups.length - 1); }} className="text-[#555] px-6">
                    Back
                  </Button>
                  <Button onClick={completeRound} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-5 text-lg" data-testid="finish-round-btn">
                    <Flag className="w-5 h-5 mr-2" /> Finish Round
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}


// ─── COMPETITIVE ROUND COMPONENT ───
function CompetitiveRound({ missionId, totalHoles, courseName, focusArea, notes, setNotes, completeRound, navigate }) {
  const [step, setStep] = useState("playing"); // "playing" | "finish"

  const OVERALL_FEEL = [
    { value: "great", label: "Great round", color: "bg-emerald-500/20 border-emerald-500/30 text-emerald-700" },
    { value: "solid", label: "Solid", color: "bg-blue-500/20 border-blue-500/30 text-blue-700" },
    { value: "average", label: "Average", color: "bg-amber-500/20 border-amber-500/30 text-[#111]" },
    { value: "tough", label: "Tough day", color: "bg-red-500/20 border-red-500/30 text-red-500" }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="px-4 py-3 border-b border-gray-200 safe-area-top">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <button onClick={() => { if (window.confirm("Exit round?")) navigate("/dashboard"); }} className="text-[#555] hover:text-[#111] p-1" data-testid="comp-exit-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center flex-1">
            <p className="text-xs text-emerald-700 uppercase tracking-wider font-medium">Competitive Round</p>
            <p className="text-[#111] text-sm font-semibold">{totalHoles} holes{courseName ? ` · ${courseName}` : ""}</p>
          </div>
          <button onClick={() => { if (window.confirm("Exit round?")) navigate("/dashboard"); }} className="text-[#555] hover:text-[#111] p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="max-w-lg mx-auto">
          <AnimatePresence mode="wait">
            {/* PLAYING STATE — minimal UI while on course */}
            {step === "playing" && (
              <motion.div key="playing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
                <div className="text-center pt-4">
                  <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <Trophy className="w-10 h-10 text-emerald-700" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#111] mb-2" data-testid="comp-playing-title">You're on the course</h1>
                  <p className="text-[#555] text-sm">{totalHoles} holes{courseName ? ` at ${courseName}` : ""}</p>
                </div>

                {/* Focus reminder */}
                {focusArea && (
                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Target className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-[#555] text-xs uppercase tracking-wider mb-1">Your focus</p>
                          <p className="text-[#111] text-sm font-medium" data-testid="comp-focus-reminder">{focusArea}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5 text-center">
                    <p className="text-[#555] text-sm mb-2">Focus on your game. When you're done, tap below.</p>
                    <p className="text-[#555] text-xs">No tracking needed during the round.</p>
                  </CardContent>
                </Card>

                <Button
                  onClick={() => setStep("finish")}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-lg font-semibold rounded-xl"
                  data-testid="comp-finish-playing-btn"
                >
                  <Flag className="w-5 h-5 mr-2" /> Finish Round
                </Button>
              </motion.div>
            )}

            {/* FINISH STATE — quick post-round log */}
            {step === "finish" && (
              <motion.div key="finish" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6">
                <div>
                  <h1 className="text-2xl font-bold text-[#111] mb-1">Round Summary</h1>
                  <p className="text-[#555] text-sm">{totalHoles} holes{courseName ? ` at ${courseName}` : ""} — nice work</p>
                </div>

                {/* Score (optional) */}
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">Score <span className="text-[#555] text-xs font-normal">(optional)</span></p>
                    <input
                      type="number" inputMode="numeric"
                      value={notes.postRound.score || ""}
                      onChange={e => setNotes(prev => ({ ...prev, postRound: { ...prev.postRound, score: e.target.value } }))}
                      placeholder={totalHoles === 18 ? "e.g., 82" : "e.g., 41"}
                      className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-lg font-semibold text-center placeholder:text-[#555]/50 focus:outline-none focus:border-emerald-500/50"
                      data-testid="comp-score-input"
                    />
                  </CardContent>
                </Card>

                {/* How did it feel? */}
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">How did it feel?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {OVERALL_FEEL.map(f => (
                        <button key={f.value} onClick={() => setNotes(prev => ({ ...prev, postRound: { ...prev.postRound, feel: f.value } }))}
                          className={`p-3 rounded-xl border text-center transition-all ${
                            notes.postRound.feel === f.value
                              ? f.color + " ring-1 ring-white/10"
                              : "bg-gray-100 border-gray-200 hover:bg-gray-200"
                          }`} data-testid={`comp-feel-${f.value}`}
                        >
                          <span className={`text-sm font-medium ${notes.postRound.feel === f.value ? "" : "text-[#111]"}`}>{f.label}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick takeaway */}
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <p className="text-[#111] font-medium mb-3">Quick takeaway <span className="text-[#555] text-xs font-normal">(optional)</span></p>
                    <textarea
                      value={notes.postRound.takeaway || ""}
                      onChange={e => setNotes(prev => ({ ...prev, postRound: { ...prev.postRound, takeaway: e.target.value } }))}
                      placeholder="e.g., Drove well but left too many putts short..."
                      className="w-full bg-gray-100 border border-gray-200 rounded-xl p-3 text-[#111] text-sm placeholder:text-[#555] resize-none h-20 focus:outline-none focus:border-emerald-500/50"
                      data-testid="comp-takeaway"
                    />
                  </CardContent>
                </Card>

                <div className="flex gap-3">
                  <Button variant="ghost" onClick={() => setStep("playing")} className="text-[#555] px-6" data-testid="comp-back-to-playing">Back</Button>
                  <Button onClick={completeRound} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-5 text-lg" data-testid="comp-submit-round-btn">
                    <Check className="w-5 h-5 mr-2" /> Log Round
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
